import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, EmptyState, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink, Loader2, BookOpen } from "lucide-react";

const AUDIENCES = ["Student", "Clinical Instructor", "DOR", "School User"];
const DISCIPLINES = ["PT", "OT", "SLP", "PTA", "OTA"];
const MANAGE_ROLES = ["Super Admin", "Campus Coordinator", "Campus Coordinator Supervisor"];

export default function Resources() {
  const { user } = useAuth();
  const canManage = MANAGE_ROLES.includes(user?.role);
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/resources", { params: canManage ? { manage: true } : {} })
    .then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [canManage]);

  const remove = async (r) => {
    try { await api.delete(`/resources/${r.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!rows) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-cc-blue" /></div>;

  return (
    <div data-testid="resources-page">
      <PageHeader title="Resources" subtitle={canManage ? "SharePoint, OneDrive and Stream links shared with students, instructors and schools" : "Videos, documents and links for you"}>
        {canManage && (
          <Button onClick={() => setEditing({ audiences: [], disciplines: [], active: true })} className="bg-cc-blue text-white" data-testid="new-resource-btn">
            <Plus className="w-4 h-4 mr-1" />New resource
          </Button>
        )}
      </PageHeader>

      {rows.length === 0 ? (
        <Card><EmptyState text={canManage ? "Nothing here yet. Add the orientation video, confidentiality video, CI resources or anything else you share." : "Nothing has been shared with you yet."} /></Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {rows.map((r) => (
            <Card key={r.id} className={r.active === false ? "opacity-60" : ""} data-testid={`resource-${r.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-cc-blue/10 flex items-center justify-center shrink-0"><BookOpen className="w-4 h-4 text-cc-blue" /></div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-zinc-900">{r.title}</p>
                  {r.description && <p className="text-sm text-zinc-600 mt-1">{r.description}</p>}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(r.audiences || []).map((a) => <span key={a} className="text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-600 rounded">{a}</span>)}
                    {(r.disciplines || []).map((d) => <span key={d} className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded">{d} only</span>)}
                    {r.active === false && <span className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-600 rounded">Hidden</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-3">
                    <a href={r.url} target="_blank" rel="noreferrer" className="text-sm text-cc-blue hover:underline flex items-center gap-1" data-testid={`resource-open-${r.id}`}>
                      Open <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                    {canManage && (
                      <>
                        <button onClick={() => setEditing(r)} className="text-zinc-400 hover:text-cc-blue" data-testid={`resource-edit-${r.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => remove(r)} className="text-zinc-400 hover:text-red-600" data-testid={`resource-delete-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && <ResourceDialog resource={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ResourceDialog({ resource, onClose, onSaved }) {
  const [form, setForm] = useState({ audiences: [], disciplines: [], ...resource });
  const [saving, setSaving] = useState(false);
  const toggle = (key, value) => {
    const list = form[key] || [];
    setForm({ ...form, [key]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value] });
  };
  const save = async () => {
    setSaving(true);
    try {
      if (form.id) await api.patch(`/resources/${form.id}`, form);
      else await api.post("/resources", form);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto cc-scroll" data-testid="resource-dialog">
        <DialogHeader><DialogTitle>{form.id ? "Edit resource" : "New resource"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Title">
            <Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Orientation & confidentiality video" data-testid="resource-title" />
          </Field>
          <Field label="Link (SharePoint, OneDrive or Microsoft Stream)">
            <Input value={form.url || ""} onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://therapymgmt.sharepoint.com/…" data-testid="resource-url" />
          </Field>
          <Field label="Short description (optional)">
            <Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="resource-description" />
          </Field>
          <Field label="Who sees it?">
            <div className="flex flex-wrap gap-3" data-testid="resource-audiences">
              {AUDIENCES.map((a) => (
                <label key={a} className="flex items-center gap-1.5 text-sm text-zinc-600">
                  <Checkbox checked={(form.audiences || []).includes(a)} onCheckedChange={() => toggle("audiences", a)} data-testid={`resource-aud-${a}`} />{a}
                </label>
              ))}
            </div>
          </Field>
          <Field label="Only for these disciplines (leave empty for all)">
            <div className="flex flex-wrap gap-3" data-testid="resource-disciplines">
              {DISCIPLINES.map((d) => (
                <label key={d} className="flex items-center gap-1.5 text-sm text-zinc-600">
                  <Checkbox checked={(form.disciplines || []).includes(d)} onCheckedChange={() => toggle("disciplines", d)} data-testid={`resource-disc-${d}`} />{d}
                </label>
              ))}
            </div>
          </Field>
          <div className="flex items-center gap-2.5">
            <Switch checked={form.active !== false} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="resource-active" />
            <span className="text-sm text-zinc-600">{form.active !== false ? "Visible" : "Hidden"}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-cc-blue text-white" data-testid="resource-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
